import {
  forwardRef,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from 'react';
import ChevronIcon from '@assets/img/icons/chevron.svg?react';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import css from './styles.module.scss';

const accordionVariants = cva('w-full flex-col', {
  variants: {
    variant: {
      bordered: css.borderedAccordion,
      default: css.defaultAccordion,
    },
  },
});

interface AccordionProps
  extends HTMLAttributes<HTMLDivElement>,
    PropsWithChildren,
    VariantProps<typeof accordionVariants> {
  head: () => ReactNode;
  classNames?: {
    head?: string;
    body?: string;
    chevron?: string;
  };
}

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  ({ head, variant = 'bordered', classNames, children, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleAccordion = (): void => setIsOpen((state) => !state);

    return (
      <div
        ref={ref}
        {...props}
        className={cn(accordionVariants({ variant }), props.className)}
      >
        <div
          onClick={toggleAccordion}
          className={cn('w-full relative', classNames?.head)}
        >
          {head()}
          <ChevronIcon
            className={cn(
              'absolute top-1/2 -translate-y-1/2 right-2 transition-all duration-300 text-white/40',
              isOpen ? 'rotate-[270deg]' : 'rotate-90',
              classNames?.chevron,
            )}
          />
        </div>
        {
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className={cn('w-full flex overflow-hidden', classNames?.body)}
              >
                {children}
              </motion.div>
            )}
          </AnimatePresence>
        }
      </div>
    );
  },
);

export { Accordion };
